import { isDefined, isEmpty, isNotDefined } from '@sgme/fp';
import { Button, Checkbox, Dropdown } from '@sgme/ui';
import { useCallback, useRef } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { v4 as uuid } from 'uuid';

import { selectCurrencyPairData } from '@/api/config/selectors.ts';
import { optionApi } from '@/api/option';
import { BACKGROUND_LIGHT_COLOR } from '@/App/tiles/CashTile/constants.ts';
import { useOptionSelector } from '@/App/tiles/Option/OptionContext.ts';
import { useQueuedDispatch } from '@/App/tiles/Option/QueueContext.tsx';
import { PredealChecksIndicator } from '@/App/tiles/shared/PredealChecksIndicator.tsx';
import ToggleProduct from '@/App/tiles/ToggleProduct.tsx';
import { ChangeClientDropDownItem } from '@/components/ClientSelector/ChangeClientDropDownItem.tsx';
import { TileOverridenClientIndicator } from '@/components/ClientSelector/TileOverridenClientIndicator.tsx';
import CurrencyPairPicker from '@/components/ui/CurrencyPairPicker/index.tsx';
import { useAppDispatch, useAppSelector } from '@/store/hooks.ts';
import { getQuote, getRfsId, isMultiLeg } from '@/store/option/selectors/optionSelectors.ts';
import { optionSlice } from '@/store/option/slice.ts';
import { resetOptionThunk } from '@/store/option/thunks/thunk.ts';
import { selectActiveTabZoomedTileId } from '@/store/tabs/slice.ts';
import { duplicateTile, removeTile } from '@/store/tiles/slice.ts';

interface OptionHeaderProps {
  tileId: string;
}

export function OptionHeader({ tileId }: OptionHeaderProps) {
  const dispatch = useAppDispatch();
  const queuedDispatch = useQueuedDispatch();
  const { formatMessage } = useIntl();

  const dropdownToggleRef = useRef<HTMLButtonElement>(null);
  const strategyPanelMode = useOptionSelector((option) => option.properties.uiState.strategyPanelMode);
  const strategyPanelIsOpened = strategyPanelMode !== 'hidden';

  const { currencyPair, clientId, rfsId } = useOptionSelector((option) => ({
    currencyPair: option.properties.currencyPair,
    clientId: option.clientId,
    rfsId: getRfsId(option),
  }));
  const isZoomed = useAppSelector(selectActiveTabZoomedTileId) === tileId;
  const currencyPairData = useAppSelector((state) => selectCurrencyPairData(state, currencyPair));

  const isMultileg = useOptionSelector((option) => isMultiLeg(option));

  const predealChecks = useOptionSelector((option) => getQuote(option)?.predealChecks);
  const globalPreDealCheck = isNotDefined(predealChecks)
    ? 'Unknown'
    : predealChecks.filter((check) => check.type === 'Icon' && check.statusHealth === 'Nok').length > 0
      ? 'Nok'
      : 'Ok';

  function handleToggleStrategyPanel() {
    dispatch(
      optionSlice.actions.setStrategyPanelMode({
        tileId,
        mode: strategyPanelMode === 'hidden' ? 'vertical' : 'hidden',
      }),
    );
  }

  function handleDuplicate() {
    dispatch(duplicateTile({ sourceTileId: tileId, newTileId: uuid(), sessionId: uuid() }));
  }

  async function handleReset() {
    await queuedDispatch(resetOptionThunk({ tileId, currencyPair, newType: 'Vanilla' }));
  }

  function handleClose() {
    dispatch(removeTile(tileId));

    if (isDefined(rfsId)) {
      dispatch(optionApi.endpoints.cancelOptionRfs.initiate({ tileId, rfsId }));
    }
  }

  const handleCurrencyPairChange = useCallback(
    (currencyPair: string) => {
      dispatch(resetOptionThunk({ tileId, currencyPair, newType: 'Vanilla', clientId }));
    },
    [tileId, clientId],
  );

  function handleSelectClient(clientId: number) {
    dispatch(optionSlice.actions.changeClient({ tileId, clientId }));
  }

  function handleClearSelectedClient() {
    dispatch(optionSlice.actions.changeClient({ tileId, clientId: undefined }));
  }

  const canToggleStrategyPanel = strategyPanelIsOpened ? !isMultileg : true;
  return (
    <>
      <TileOverridenClientIndicator clientId={clientId} onRemoveOverride={handleClearSelectedClient} />
      <div style={{ display: 'grid', gridTemplateColumns: '1fr auto 1fr', alignItems: 'center' }} className="py-2">
        <div className="d-flex align-items-center">
          {!isZoomed && <ToggleProduct tileId={tileId} />}
          <PredealChecksIndicator predealChecks={predealChecks} globalPreDealCheck={globalPreDealCheck} />
        </div>

        <CurrencyPairPicker tradeType="Option" value={currencyPair} onChange={handleCurrencyPairChange} />

        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 8 }}>
          {currencyPairData?.notDeliverable && (
            <Checkbox
              label={formatMessage({ id: 'CashTile.NDOCheckbox.label' })}
              checked={currencyPairData?.notDeliverable ?? false}
              size="sm"
              disabled
              dataE2e="ndo-checkbox"
            />
          )}

          {!isZoomed && (
            <>
              <Button
                purpose="flat"
                color="secondary"
                disabled={!canToggleStrategyPanel}
                onClick={handleToggleStrategyPanel}
                className="p-0 mx-1"
                style={{ transform: strategyPanelIsOpened ? '' : 'scale(-1,1)' }}
                data-e2e="strategy-toggle"
              >
                <i className="icon icon-sm">menu_open</i>
              </Button>

              <Dropdown color={BACKGROUND_LIGHT_COLOR} direction="auto" size="md" data-e2e="tile-menu">
                <Dropdown.Toggle className="btn-icon" data-e2e="tileMenu-btn">
                  <i className="icon" ref={dropdownToggleRef}>
                    more_vert
                  </i>
                </Dropdown.Toggle>

                <Dropdown.Menu>
                  <ChangeClientDropDownItem triggerRef={dropdownToggleRef} onSelectClient={handleSelectClient} />

                  <Dropdown.Item
                    onClick={handleDuplicate}
                    state={isEmpty(currencyPair) ? 'disabled' : undefined}
                    data-e2e="duplicate-tile-menu"
                  >
                    <FormattedMessage id="option.header.menu.duplicate" />
                  </Dropdown.Item>
                  <Dropdown.Item
                    onClick={handleReset}
                    state={isEmpty(currencyPair) ? 'disabled' : undefined}
                    data-e2e="resetTile-menu-item"
                  >
                    <FormattedMessage id="option.header.menu.reset" />
                  </Dropdown.Item>
                  <Dropdown.Item onClick={handleClose} data-e2e="close-tile-menu">
                    <FormattedMessage id="option.header.menu.close" />
                  </Dropdown.Item>
                </Dropdown.Menu>
              </Dropdown>
            </>
          )}
        </div>
      </div>
    </>
  );
}
